import React, { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type {
  NotificationTarget,
  DueNotificationTarget,
} from "../types/types.ts";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";

const PRIORITIES = ["高", "中", "低", "なし"];

const DUE_TARGET_OPTIONS: { value: DueNotificationTarget; label: string }[] = [
  { value: "both", label: "開始日と期限日" },
  { value: "start", label: "開始日のみ" },
  { value: "end", label: "期限日のみ" },
];

interface NotificationTargetFilterProps {
  value: NotificationTarget;
  onChange: (next: NotificationTarget) => void;
  /** ルームのアクティブなプロジェクト */
  todoLists: { id: string; name: string }[];
  /** 選択肢にするステータス名（「完了」「非表示」を除いたもの） */
  statuses: string[];
  /** 期限日の通知のみ: 対象の日付 */
  dueTarget?: DueNotificationTarget;
  onDueTargetChange?: (next: DueNotificationTarget) => void;
  disabled?: boolean;
  color: PaletteColor;
}

/**
 * 通知の「詳細な設定」（F018）。既定は閉じた状態で、プロジェクト・ステータス・重要度を絞り込む。
 * 値は除外リストで持ち、画面上は「選択中 = 全体 − 除外」として表示する。
 */
export const NotificationTargetFilter: React.FC<NotificationTargetFilterProps> = ({
  value,
  onChange,
  todoLists,
  statuses,
  dueTarget,
  onDueTargetChange,
  disabled = false,
  color,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const listOptions = todoLists
    .map((l) => ({ key: Number(l.id), label: l.name }))
    .filter((o) => o.key > 0);

  // 削除済みプロジェクト等、選択肢にない除外値は「変更あり」の判定に含めない
  const isChanged =
    (dueTarget !== undefined && dueTarget !== "both") ||
    listOptions.some((o) => value.excludedListIds.includes(o.key)) ||
    statuses.some((s) => value.excludedStatuses.includes(s)) ||
    PRIORITIES.some((p) => value.excludedPriorities.includes(p));

  const toggleExcluded = <T,>(list: T[], item: T): T[] =>
    list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

  const resetAll = () =>
    onChange({
      ...value,
      excludedListIds: [],
      excludedStatuses: [],
      excludedPriorities: [],
    });

  const accent = PaletteColorHex[color];

  return (
    <div className={disabled ? "opacity-50 pointer-events-none" : ""}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="flex items-center gap-0.5 text-sm font-medium hover:underline"
        style={{ color: accent }}
        aria-expanded={isOpen}
        disabled={disabled}
      >
        {isOpen ? (
          <ExpandMoreIcon fontSize="small" />
        ) : (
          <ChevronRightIcon fontSize="small" />
        )}
        {isChanged ? "詳細な設定（変更あり）" : "詳細な設定"}
      </button>

      {isOpen && (
        <div className="mt-2 ml-5 space-y-4 text-sm">
          {dueTarget !== undefined && onDueTargetChange && (
            <FilterRow label="対象の日付">
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {DUE_TARGET_OPTIONS.map((o) => (
                  <label key={o.value} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      checked={dueTarget === o.value}
                      onChange={() => onDueTargetChange(o.value)}
                      style={{ accentColor: accent }}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
            </FilterRow>
          )}

          <CheckGroup
            label="プロジェクト"
            options={listOptions}
            excluded={value.excludedListIds}
            onToggle={(key) =>
              onChange({
                ...value,
                excludedListIds: toggleExcluded(value.excludedListIds, key),
              })
            }
            accent={accent}
          />
          <CheckGroup
            label="ステータス"
            options={statuses.map((s) => ({ key: s, label: s }))}
            excluded={value.excludedStatuses}
            onToggle={(key) =>
              onChange({
                ...value,
                excludedStatuses: toggleExcluded(value.excludedStatuses, key),
              })
            }
            accent={accent}
          />
          <CheckGroup
            label="重要度"
            options={PRIORITIES.map((p) => ({ key: p, label: p }))}
            excluded={value.excludedPriorities}
            onToggle={(key) =>
              onChange({
                ...value,
                excludedPriorities: toggleExcluded(value.excludedPriorities, key),
              })
            }
            accent={accent}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            「完了」「非表示」のタスクは常に通知されません。
          </p>
          {isChanged && (
            <button
              type="button"
              onClick={resetAll}
              className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
            >
              すべて選択に戻す
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const FilterRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">{label}</div>
    {children}
  </div>
);

function CheckGroup<K extends string | number>({
  label,
  options,
  excluded,
  onToggle,
  accent,
}: {
  label: string;
  options: { key: K; label: string }[];
  excluded: K[];
  onToggle: (key: K) => void;
  accent: string;
}) {
  const noneSelected = options.length > 0 && options.every((o) => excluded.includes(o.key));
  return (
    <FilterRow label={label}>
      {options.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">選択肢がありません</p>
      ) : (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {options.map((o) => (
            <label key={o.key} className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={!excluded.includes(o.key)}
                onChange={() => onToggle(o.key)}
                style={{ accentColor: accent }}
              />
              <span className="break-all">{o.label}</span>
            </label>
          ))}
        </div>
      )}
      {noneSelected && (
        <p className="mt-1 text-xs text-red-500">対象がありません</p>
      )}
    </FilterRow>
  );
}
